import { useCallback, useEffect, useState } from 'react';
import type { ArrayFromExperiments, Experiments } from '@cbhq/experiments';

import {
  ApiEnvironmentMapping,
  enableAllExperiments,
  targetEnvironment,
} from '../../../configs/environments';
import { workspaceExperimentConfig } from '../appExperiments';

const USER_SUBJECT_TYPE = 1;
const UUID_SUBJECT_TYPE = 6;
const WEB_CLIENT_TYPE = 3;

type CiferGroup = { test: string; group: string; isTracked: boolean };
type CiferResponse = { groups: CiferGroup[] };

// Local dev override: CIFER isn't reachable locally, so when ENABLE_ALL_EXPERIMENTS
// is set (see configs/environments) every workspace experiment is forced to
// `treatment`. This lets all gated features (Activity, Security, etc.) render
// together while developing, without a CIFER call.
const LOCAL_TREATMENT_EXPERIMENTS: ArrayFromExperiments<Experiments> = Object.keys(
  workspaceExperimentConfig,
).map((name) => ({ name, group: 'treatment', skipTracking: true }));

// CIFER only supports dev and prod environments.
// Local uses empty host (reverse proxy). Staging routes through workspace-staging.
function getCiferHost(): string {
  if (targetEnvironment === 'local') return '';

  if (targetEnvironment === 'production') {
    return ApiEnvironmentMapping.production.host;
  }

  if (targetEnvironment === 'staging') {
    return ApiEnvironmentMapping.staging.host;
  }

  return ApiEnvironmentMapping.development.host;
}

const baseUrl = `${getCiferHost()}/api/v3/coinbase.experiment.ExperimentService/ListSplitTestGroups`;

function buildUrl(subjectId: string, subjectType: number): string {
  const query = {
    subject_id: subjectId,
    client: { type: WEB_CLIENT_TYPE },
    subject_type: subjectType,
  };
  return `${baseUrl}?${new URLSearchParams({ q: btoa(JSON.stringify(query)) })}`;
}

async function fetchCiferExperiments(
  subjectId: string,
  subjectType: number,
): Promise<CiferResponse | undefined> {
  const res = await fetch(buildUrl(subjectId, subjectType), {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'x-enabled-features': 'targeting',
    },
  });

  if (!res.ok) return undefined;

  return (await res.json()) as CiferResponse;
}

export function useGetCiferExperiments(subjectId: string): {
  loading: boolean;
  experiments: ArrayFromExperiments<Experiments>;
} {
  const [experiments, setExperiments] = useState<ArrayFromExperiments<Experiments>>(
    enableAllExperiments ? LOCAL_TREATMENT_EXPERIMENTS : [],
  );
  // Local never calls CIFER (see fetchExperiments), so there's nothing to wait
  // for: assignments are final from the first render (all treatment with
  // ENABLE_ALL_EXPERIMENTS, otherwise none, i.e. control).
  const [loading, setLoading] = useState(targetEnvironment !== 'local');

  const fetchExperiments = useCallback(async () => {
    if (!subjectId || targetEnvironment === 'local') {
      return;
    }

    setLoading(true);

    try {
      const uuidExperiments = await fetchCiferExperiments(subjectId, UUID_SUBJECT_TYPE);
      const userExperiments = await fetchCiferExperiments(subjectId, USER_SUBJECT_TYPE);
      const known = Object.keys(workspaceExperimentConfig);

      setExperiments(
        [...(uuidExperiments?.groups ?? []), ...(userExperiments?.groups ?? [])]
          .filter((g) => known.includes(g.test))
          .map((g) => ({ name: g.test, group: g.group, skipTracking: !g.isTracked })),
      );
    } catch {
      // Silently fall back to empty experiments on fetch failure (e.g. local dev CORS)
    } finally {
      setLoading(false);
    }
  }, [subjectId]);

  useEffect(() => {
    fetchExperiments();
  }, [fetchExperiments]);

  return { loading, experiments };
}
